import { useCallback, useEffect, useState } from "react";
import { loadMediaUrl, peekMediaUrl } from "./mediaUrlCache";

/**
 * Resolve a chat attachment to a signed URL usable as an `<img>`/`<video>`/
 * `<audio>`/`<iframe>` src.
 *
 * Pass `enabled: false` to defer the lookup until the media is actually wanted.
 *
 * @param {Object} attachment - Normalized attachment (see utils/commsAttachments).
 * @param {Object} [options]
 * @param {boolean} [options.enabled=true] - Whether to resolve now.
 * @returns {{ url: string, loading: boolean, error: Error|null, refresh: () => void }}
 *   `refresh` re-mints the URL — wire it to a media element's onError so a SAS
 *   that expired while the thread sat open recovers on the next interaction.
 */
export function useMediaUrl(attachment, { enabled = true } = {}) {
  const blobPath = attachment?.blobPath || "";
  // Legacy rows predating blob_path can only be addressed by data_url.
  const fallbackUrl = blobPath ? "" : attachment?.url || "";

  const [attempt, setAttempt] = useState(0);
  const [state, setState] = useState(() => ({
    url: peekMediaUrl(blobPath) || fallbackUrl,
    loading: false,
    error: null,
  }));

  useEffect(() => {
    if (!enabled) return undefined;

    if (!blobPath) {
      setState({
        url: fallbackUrl,
        loading: false,
        error: fallbackUrl ? null : new Error("Attachment is unavailable."),
      });
      return undefined;
    }

    // A refresh must skip the cache; a first pass may use it.
    const cached = attempt === 0 ? peekMediaUrl(blobPath) : null;
    if (cached) {
      setState({ url: cached, loading: false, error: null });
      return undefined;
    }

    let cancelled = false;
    setState((prev) => ({ ...prev, loading: true, error: null }));

    loadMediaUrl(blobPath, { force: attempt > 0 })
      .then((url) => {
        if (!cancelled) setState({ url, loading: false, error: null });
      })
      .catch((err) => {
        if (!cancelled) setState({ url: "", loading: false, error: err });
      });

    return () => {
      cancelled = true;
    };
  }, [blobPath, fallbackUrl, enabled, attempt]);

  const refresh = useCallback(() => setAttempt((n) => n + 1), []);

  return { ...state, refresh };
}

/**
 * Save an attachment to disk. Fetching the signed URL directly is allowed —
 * that request goes straight to storage with a real Origin, which the CORS
 * rule matches — and going via a blob keeps the original filename.
 *
 * @param {Object} attachment - Normalized attachment.
 * @returns {Promise<void>}
 */
export async function downloadAttachment(attachment) {
  const signedUrl = attachment?.blobPath
    ? await loadMediaUrl(attachment.blobPath)
    : attachment?.url;
  if (!signedUrl) throw new Error("Attachment is unavailable.");

  const response = await fetch(signedUrl);
  if (!response.ok) throw new Error("Attachment could not be downloaded.");

  const objectUrl = URL.createObjectURL(await response.blob());
  try {
    const anchor = document.createElement("a");
    anchor.href = objectUrl;
    anchor.download = attachment.fileName || "attachment";
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

export default useMediaUrl;
